import { useMemo } from 'react';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { formatCurrency } from 'src/utils/numberFormatter';

import * as styles from './PartialPaymentInput.css';

type PartialPaymentInputProps = {
  disabled: boolean;
  totalAmount: number;
  currencyCode: string;
  isPartialPayment: boolean;
  partialAmount: number;
  onIsPartialPaymentChange: (value: boolean) => void;
  onPartialAmountChange: (amount: number) => void;
};

export const PartialPaymentInput = ({
  disabled,
  totalAmount,
  currencyCode,
  isPartialPayment,
  partialAmount,
  onIsPartialPaymentChange,
  onPartialAmountChange,
}: PartialPaymentInputProps) => {
  const ofText = useContent(ContentId.Of);

  const isInvalid = useMemo(
    () =>
      isPartialPayment && (partialAmount <= 0 || partialAmount >= totalAmount),
    [isPartialPayment, partialAmount, totalAmount]
  );
  const activePct = useMemo(
    () =>
      [25, 50, 75]
        .find(
          (pct) =>
            Math.abs(partialAmount - Math.round(totalAmount * pct) / 100) <
            0.001
        )
        ?.toString() ?? '',
    [partialAmount, totalAmount]
  );

  const totalAmountText = useMemo(
    () => formatCurrency(totalAmount, currencyCode),
    [currencyCode, totalAmount]
  );

  return (
    <Stack direction="column" gap="m">
      <RadioGroup
        value={isPartialPayment ? 'partial' : 'full'}
        onValueChange={(val) => {
          const isPartial = val === 'partial';
          onIsPartialPaymentChange(isPartial);
          onPartialAmountChange(totalAmount);
        }}
        disabled={disabled}
      >
        <Radio
          variant="outline"
          value="full"
          label="Full Payment"
          disabled={disabled}
        />
        <Radio
          alignItems="start"
          value="partial"
          label="Partial Payment"
          variant="outline"
          disabled={disabled}
          labelSubtext={
            isPartialPayment ? (
              <Stack
                direction="column"
                gap="m"
                style={{ marginTop: vars.spacing.m }}
              >
                <Stack width="full" alignItems="center" gap="m">
                  <Stack gap="s">
                    <span className={styles.quickFillLabel}>
                      <Content id={ContentId.QuickFill} />
                    </span>
                    <ToggleGroup
                      value={activePct}
                      disabled={disabled}
                      onValueChange={(val: string) => {
                        if (!val) return;
                        onPartialAmountChange(
                          Math.round(totalAmount * parseInt(val)) / 100
                        );
                      }}
                      options={[25, 50, 75].map((pct) => ({
                        value: pct.toString(),
                        children: `${pct}%`,
                      }))}
                    />
                  </Stack>
                  <Stack alignItems="center" gap="s">
                    <span className={styles.currencyLabel}>{currencyCode}</span>
                    <PosTextField
                      type="number"
                      min={0}
                      max={totalAmount}
                      step="0.01"
                      value={partialAmount || ''}
                      placeholder="0.00"
                      disabled={disabled}
                      errorState={isInvalid ? 'error' : undefined}
                      rootProps={{ className: styles.amountInputRoot }}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        onPartialAmountChange(isNaN(val) ? 0 : val);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className={styles.totalLabel}>
                      {`${ofText} ${totalAmountText}`}
                    </span>
                  </Stack>
                </Stack>

                {isInvalid && (
                  <span className={styles.errorText}>
                    <FormatContent
                      id={FormatContentId.PartialPaymentValidationMessage}
                      params={[totalAmountText]}
                    />
                  </span>
                )}
              </Stack>
            ) : null
          }
        />
      </RadioGroup>
    </Stack>
  );
};
